<template>
    <div class="task-reports">
        <div class="reports-header">
            <h2 class="reports-title">Görev Raporları</h2>
            <div class="header-controls">
                <div class="workflow-selector">
                    <a-select
                        v-model="selectedWorkflowId"
                        placeholder="Workflow seçiniz"
                        style="width: 300px;"
                        :disabled="isLoading"
                        @change="handleWorkflowChange"
                    >
                        <a-select-option
                            v-for="workflow in availableWorkflows"
                            :key="workflow.value"
                            :value="workflow.value"
                        >
                            {{ workflow.label }}
                        </a-select-option>
                    </a-select>
                </div>

                <div class="filter-controls" v-if="selectedWorkflowId">
                    <a-button
                        type="default"
                        @click="showFilters = !showFilters"
                        :class="{ 'active': hasActiveFilters }"
                    >
                        <span class="material-icons">tune</span>
                        Filtreler
                        <span v-if="hasActiveFilters" class="filter-badge">{{ activeFilterCount }}</span>
                    </a-button>

                    <a-button
                        v-if="hasActiveFilters"
                        type="link"
                        @click="clearFilters"
                        style="padding: 4px 8px;"
                    >
                        <span class="material-icons">clear</span>
                        Temizle
                    </a-button>
                </div>
            </div>
        </div>

        <div v-if="showFilters && selectedWorkflowId" class="filters-panel">
            <div class="filter-row">
                <div class="filter-group">
                    <label class="filter-label">Tarih Aralığı:</label>
                    <a-range-picker
                        v-model="dateRange"
                        style="width: 280px;"
                        placeholder="Başlangıç - Bitiş"
                        format="DD/MM/YYYY"
                        @change="handleDateRangeChange"
                    />
                </div>

                <div class="filter-group">
                    <label class="filter-label">Atanan Kişiler:</label>
                    <a-select
                        v-model="selectedAssignees"
                        mode="multiple"
                        placeholder="Kullanıcı seçiniz"
                        style="width: 280px;"
                        @change="handleAssigneesChange"
                    >
                        <a-select-option value="notAppointed">Atanmamış</a-select-option>
                        <a-select-option
                            v-for="user in filteredUsersAvatar"
                            :key="user._id"
                            :value="user._id"
                        >
                            {{ user.name }}
                        </a-select-option>
                    </a-select>
                </div>

                <div class="filter-group">
                    <label class="filter-label">Öncelik:</label>
                    <a-select
                        v-model="selectedPriorities"
                        mode="multiple"
                        placeholder="Öncelik seçiniz"
                        style="width: 200px;"
                        @change="handlePrioritiesChange"
                    >
                        <a-select-option value="highest">Highest</a-select-option>
                        <a-select-option value="high">High</a-select-option>
                        <a-select-option value="medium">Medium</a-select-option>
                        <a-select-option value="low">Low</a-select-option>
                        <a-select-option value="lowest">Lowest</a-select-option>
                    </a-select>
                </div>
            </div>
        </div>

        <div class="reports-content">
            <div v-if="!selectedWorkflowId" class="no-workflow-selected">
                <div class="empty-state">
                    <span class="material-icons empty-icon">bar_chart</span>
                    <h3>Rapor Görüntülemek İçin Workflow Seçin</h3>
                    <p>Görev raporlarını görüntülemek için yukarıdan bir workflow seçiniz.</p>
                </div>
            </div>

            <div v-else-if="isLoading" class="loading-state">
                <div class="custom-loading">
                    <svg class="loading-svg" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                        <circle class="loading-circle-bg" cx="60" cy="60" r="50" fill="none" stroke="#e5e7eb" stroke-width="6"/>
                        <circle class="loading-circle-progress" cx="60" cy="60" r="50" fill="none" stroke="#3b82f6" stroke-width="6" stroke-linecap="round"/>

                        <g class="task-icons">
                            <rect x="40" y="35" width="40" height="50" rx="4" fill="#f8fafc" stroke="#cbd5e1" stroke-width="1.5"/>

                            <rect x="45" y="42" width="8" height="8" rx="1" fill="#10b981" stroke="#10b981"/>
                            <path d="M47 45 L49 47 L52 44" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round"/>
                            <line x1="56" y1="46" x2="72" y2="46" stroke="#374151" stroke-width="1.5" stroke-linecap="round"/>

                            <rect x="45" y="54" width="8" height="8" rx="1" fill="#10b981" stroke="#10b981"/>
                            <path d="M47 57 L49 59 L52 56" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round"/>
                            <line x1="56" y1="58" x2="70" y2="58" stroke="#374151" stroke-width="1.5" stroke-linecap="round"/>

                            <rect x="45" y="66" width="8" height="8" rx="1" fill="white" stroke="#cbd5e1"/>
                            <line x1="56" y1="70" x2="68" y2="70" stroke="#9ca3af" stroke-width="1.5" stroke-linecap="round"/>

                            <rect x="45" y="78" width="8" height="8" rx="1" fill="white" stroke="#cbd5e1"/>
                            <line x1="56" y1="82" x2="65" y2="82" stroke="#9ca3af" stroke-width="1.5" stroke-linecap="round"/>
                        </g>

                        <circle class="floating-dot dot-1" cx="25" cy="30" r="3" fill="#f59e0b"/>
                        <circle class="floating-dot dot-2" cx="95" cy="40" r="2.5" fill="#8b5cf6"/>
                        <circle class="floating-dot dot-3" cx="20" cy="90" r="2" fill="#ef4444"/>
                        <circle class="floating-dot dot-4" cx="100" cy="85" r="3" fill="#10b981"/>
                    </svg>

                    <div class="loading-text">
                        <div class="loading-title">Rapor Hazırlanıyor</div>
                        <div class="loading-subtitle">Görev verileri analiz ediliyor...</div>
                    </div>
                </div>
            </div>

            <div v-else-if="reportData" class="reports-data">
                <div class="stats-cards">
                    <div class="stat-card completed">
                        <div class="stat-icon">
                            <span class="material-icons">done_all</span>
                        </div>
                        <div class="stat-content">
                            <div class="stat-label">{{ dateRangeText }}</div>
                            <div class="stat-value">{{ reportData.dateRangeStats.completedTasks }} görev kapatıldı</div>
                        </div>
                    </div>

                    <div class="stat-card updated">
                        <div class="stat-icon">
                            <span class="material-icons">update</span>
                        </div>
                        <div class="stat-content">
                            <div class="stat-label">{{ dateRangeText }}</div>
                            <div class="stat-value">{{ reportData.dateRangeStats.updatedTasks }} görev güncellendi</div>
                        </div>
                    </div>

                    <div class="stat-card created">
                        <div class="stat-icon">
                            <span class="material-icons">add_task</span>
                        </div>
                        <div class="stat-content">
                            <div class="stat-label">{{ dateRangeText }}</div>
                            <div class="stat-value">{{ reportData.dateRangeStats.createdTasks }} görev oluşturuldu</div>
                        </div>
                    </div>

                    <div class="stat-card deadline">
                        <div class="stat-icon">
                            <span class="material-icons">schedule</span>
                        </div>
                        <div class="stat-content">
                            <div class="stat-label">Yaklaşan Deadlinelar</div>
                            <div class="stat-value">{{ upcomingDeadlineText }}</div>
                        </div>
                    </div>
                </div>

                <div class="report-cards">
                    <div class="report-card pipeline-overview">
                        <div class="card-header">
                            <div class="header-content">
                                <h3 class="card-title">Durum genel bakışı</h3>
                                <p class="card-subtitle">Biletlerinizin durumunu anlık olarak görün.</p>
                            </div>
                            <div class="header-icon">
                                <span class="material-icons">donut_large</span>
                            </div>
                        </div>

                        <div class="card-content">
                            <div class="chart-container">
                                <div class="simple-pie-chart">
                                    <div class="pie-container">
                                        <div class="pie-chart" :style="{ background: pieChartGradient }">
                                            <div class="pie-center">
                                                <div class="total-number">{{ reportData.statusOverview.totalTasks }}</div>
                                                <div class="total-label">Toplam bilet</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="legend">
                                    <div
                                        v-for="(item, index) in legendItems"
                                        :key="index"
                                        class="legend-item"
                                        :style="{ animationDelay: (index * 0.1) + 's' }"
                                    >
                                        <div class="legend-color" :style="{ backgroundColor: item.color }"></div>
                                        <div class="legend-text">
                                            <span class="legend-label">{{ item.label }}:</span>
                                            <span class="legend-value">{{ item.value }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="report-card priority-overview">
                        <div class="card-header">
                            <div class="header-content">
                                <h3 class="card-title">Öncelik Dağılımı</h3>
                            </div>
                            <div class="header-icon">
                                <span class="material-icons">priority_high</span>
                            </div>
                        </div>

                        <div class="card-content">
                            <div class="bar-chart-container">
                                <div class="chart-wrapper">
                                    <div class="y-axis">
                                        <div class="y-label" v-for="tick in yAxisTicks" :key="tick">{{ tick }}</div>
                                    </div>

                                    <div class="chart-area">
                                        <div class="grid-lines">
                                            <div
                                                v-for="tick in yAxisTicks"
                                                :key="tick"
                                                class="grid-line"
                                            ></div>
                                        </div>

                                        <div class="bars-container">
                                            <div
                                                v-for="(item, index) in priorityChartData"
                                                :key="index"
                                                class="bar-item"
                                                :style="{ animationDelay: (index * 0.1) + 's' }"
                                            >
                                                <div class="bar-wrapper">
                                                    <div
                                                        class="bar"
                                                        :class="item.priority"
                                                        :style="{
                                                            height: item.heightPercentage + '%',
                                                            backgroundColor: item.color
                                                        }"
                                                    >
                                                        <div class="bar-value">{{ item.count }}</div>
                                                    </div>
                                                </div>
                                                <div class="bar-label">{{ item.label }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="report-cards">
                    <div class="report-card team-workload">
                        <div class="card-header">
                            <div class="header-content">
                                <h3 class="card-title">Takım iş yükü</h3>
                            </div>
                            <div class="header-icon">
                                <span class="material-icons">groups</span>
                            </div>
                        </div>

                        <div class="card-content">
                            <div class="team-list">
                                <div
                                    v-for="(member, index) in teamWorkloadData"
                                    :key="index"
                                    class="team-member"
                                    :style="{ animationDelay: (index * 0.1) + 's' }"
                                    v-tippy="{
                                        content: `${member.user.name}: ${member.taskCount} görev (${member.percentage}%)`,
                                        placement: 'top'
                                    }"
                                >
                                    <div class="member-info">
                                        <div class="member-avatar">
                                            <img
                                                v-if="member.user.gravatar"
                                                :src="member.user.gravatar"
                                                :alt="member.user.name"
                                                class="avatar-image"
                                            />
                                            <div
                                                v-else
                                                class="avatar-placeholder"
                                                :class="{ 'unassigned': !member.userId }"
                                            >
                                                <span v-if="member.userId" class="avatar-initials">
                                                    {{ getInitials(member.user.name) }}
                                                </span>
                                                <span v-else class="material-icons unassigned-icon">person_off</span>
                                            </div>
                                        </div>
                                        <div class="member-details">
                                            <div class="member-name">{{ member.user.name }}</div>
                                            <div class="member-email" v-if="member.user.email">{{ member.user.email }}</div>
                                        </div>
                                    </div>

                                    <div class="workload-info">
                                        <div class="workload-percentage">{{ member.percentage }}%</div>
                                        <div class="workload-bar">
                                            <div
                                                class="workload-progress"
                                                :style="{
                                                    width: member.percentage + '%',
                                                    backgroundColor: getWorkloadColor(member.percentage)
                                                }"
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else-if="error" class="error-state">
                <div class="error-content">
                    <span class="material-icons error-icon">error</span>
                    <h3>Rapor Yüklenirken Hata Oluştu</h3>
                    <p>{{ error }}</p>
                    <a-button type="primary" @click="loadReports">
                        <span class="material-icons">refresh</span>
                        Tekrar Dene
                    </a-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
    name: 'TaskReports',
    data() {
        return {
            selectedWorkflowId: null,
            reportData: null,
            isLoading: false,
            error: null,
            showFilters: false,
            dateRange: null,
            selectedAssignees: [],
            selectedPriorities: [],
        };
    },
    computed: {
        ...mapState({
            workflows: (state) => state.tasks.workflows,
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            usersAvatar: (state) => state.user.users.data,
        }),
        availableWorkflows() {
            console.log('Workflows state:', this.workflows);

            if (Array.isArray(this.workflows)) {
                return this.workflows.map((workflow) => ({
                    value: workflow._id,
                    label: workflow.title,
                }));
            }

            return Object.keys(this.workflows)
                .map((workflowId) => ({
                    value: workflowId,
                    label: this.workflows[workflowId].title,
                }));
        },

        filteredUsersAvatar() {
            if (!this.selectedWorkflowId) return this.usersAvatar;

            const workflowsObj = Array.isArray(this.workflows)
                ? this.workflows.reduce((acc, workflow) => {
                    acc[workflow._id] = workflow;
                    return acc;
                }, {})
                : this.workflows;

            const selectedWorkflow = workflowsObj[this.selectedWorkflowId];

            if (!selectedWorkflow || !selectedWorkflow.visibility || selectedWorkflow.visibility.users.length === 0) {
                return this.usersAvatar;
            }

            const users = selectedWorkflow.visibility.users;
            const usersAvatar = users.map((userId) => this.users[userId]).filter(user => user);
            return usersAvatar;
        },

        progressRingData() {
            if (!this.reportData || !this.reportData.statusOverview) return [];

            const pipelineDistribution = this.reportData.statusOverview.pipelineDistribution;
            const totalTasks = this.reportData.statusOverview.totalTasks;

            if (totalTasks === 0) return [];

            const colors = {
                'Todo': '#3b82f6',
                'In Progress': '#10b981',
                'Approval': '#f59e0b',
                'Completed': '#8b5cf6',
                'Done': '#059669',
                'Bug': '#ef4444',
                'Testing': '#f97316',
            };

            return Object.entries(pipelineDistribution)
                .map(([pipeline, count]) => ({
                    pipeline,
                    count,
                    percentage: Math.round((count / totalTasks) * 100),
                    color: colors[pipeline] || '#94a3b8'
                }))
                .sort((a, b) => b.count - a.count);
        },

        pieChartGradient() {
            if (!this.reportData || !this.reportData.statusOverview) return '';

            const pipelineDistribution = this.reportData.statusOverview.pipelineDistribution;
            const totalTasks = this.reportData.statusOverview.totalTasks;

            if (totalTasks === 0) return 'conic-gradient(#f1f5f9 0deg 360deg)';

            const colors = {
                'Todo': '#3b82f6',
                'In Progress': '#10b981',
                'Approval': '#f59e0b',
                'Completed': '#8b5cf6',
                'Done': '#059669',
                'Bug': '#ef4444',
                'Testing': '#f97316',
            };

            let currentDegree = 0;
            const gradientParts = [];

            Object.entries(pipelineDistribution).forEach(([pipeline, count]) => {
                const percentage = (count / totalTasks) * 100;
                const degrees = (percentage / 100) * 360;
                const color = colors[pipeline] || '#94a3b8';

                gradientParts.push(`${color} ${currentDegree}deg ${currentDegree + degrees}deg`);
                currentDegree += degrees;
            });

            return `conic-gradient(${gradientParts.join(', ')})`;
        },

        chartSegments() {
            if (!this.reportData || !this.reportData.statusOverview) return [];

            const pipelineDistribution = this.reportData.statusOverview.pipelineDistribution;
            const totalTasks = this.reportData.statusOverview.totalTasks;

            if (totalTasks === 0) return [];

            const colors = {
                'Todo': '#3b82f6',
                'In Progress': '#10b981',
                'Approval': '#f59e0b',
                'Completed': '#8b5cf6',
                'Done': '#059669',
                'Bug': '#ef4444',
                'Testing': '#f97316',
            };

            const radius = 70;
            const circumference = 2 * Math.PI * radius;
            let rotationOffset = 0;

            console.log('Chart Debug:', {
                pipelineDistribution,
                totalTasks,
                circumference
            });

            return Object.entries(pipelineDistribution).map(([pipeline, count]) => {
                const percentage = count / totalTasks;
                const arcLength = circumference * percentage;
                const gapLength = circumference - arcLength;

                const dashArray = `${arcLength} ${gapLength}`;

                const dashOffset = -rotationOffset;

                console.log(`${pipeline}:`, {
                    count,
                    percentage: (percentage * 100).toFixed(1) + '%',
                    arcLength: arcLength.toFixed(2),
                    dashArray,
                    dashOffset: dashOffset.toFixed(2)
                });

                rotationOffset += arcLength

                return {
                    pipeline,
                    count,
                    percentage,
                    color: colors[pipeline] || '#94a3b8',
                    dashArray,
                    dashOffset
                };
            });
        },

        legendItems() {
            if (!this.reportData || !this.reportData.statusOverview) return [];

            const pipelineDistribution = this.reportData.statusOverview.pipelineDistribution;
            const colors = {
                'Todo': '#3b82f6',
                'In Progress': '#10b981',
                'Approval': '#f59e0b',
                'Completed': '#8b5cf6',
                'Done': '#059669',
                'Bug': '#ef4444',
                'Testing': '#f97316',
            };

            return Object.entries(pipelineDistribution).map(([pipeline, count]) => ({
                label: pipeline,
                value: count,
                color: colors[pipeline] || '#94a3b8'
            }));
        },

        priorityChartData() {
            if (!this.reportData || !this.reportData.priorityDistribution) return [];

            const priorityDistribution = this.reportData.priorityDistribution;
            const maxCount = Math.max(...Object.values(priorityDistribution));

            // Öncelik sırası ve renkleri
            const priorityOrder = ['highest', 'high', 'medium', 'low', 'lowest'];
            const priorityColors = {
                'highest': '#dc2626',
                'high': '#ea580c',
                'medium': '#d97706',
                'low': '#65a30d',
                'lowest': '#16a34a'
            };

            const priorityLabels = {
                'highest': 'Highest',
                'high': 'High',
                'medium': 'Medium',
                'low': 'Low',
                'lowest': 'Lowest'
            };

            return priorityOrder.map(priority => {
                const count = priorityDistribution[priority] || 0;
                const heightPercentage = maxCount > 0 ? (count / maxCount) * 100 : 0;

                return {
                    priority,
                    count,
                    label: priorityLabels[priority],
                    color: priorityColors[priority],
                    heightPercentage
                };
            }).filter(item => item.count > 0);
        },

        yAxisTicks() {
            if (!this.reportData || !this.reportData.priorityDistribution) return [0];

            const maxCount = Math.max(...Object.values(this.reportData.priorityDistribution));
            const tickCount = 5;
            const step = Math.ceil(maxCount / tickCount);

            const ticks = [];
            for (let i = 0; i <= tickCount; i++) {
                ticks.push(i * step);
            }

            return ticks.reverse();
        },

        teamWorkloadData() {
            if (!this.reportData || !this.reportData.teamWorkload) return [];

            const teamWorkload = this.reportData.teamWorkload;
            const totalTasks = this.reportData.statusOverview.totalTasks;

            return teamWorkload.map(member => {
                const percentage = totalTasks > 0 ? Math.round((member.taskCount / totalTasks) * 100) : 0;

                return {
                    ...member,
                    percentage
                };
            }).sort((a, b) => b.taskCount - a.taskCount);
        },

        hasActiveFilters() {
            return this.dateRange ||
                   this.selectedAssignees.length > 0 ||
                   this.selectedPriorities.length > 0;
        },

        activeFilterCount() {
            let count = 0;
            if (this.dateRange) count++;
            if (this.selectedAssignees.length > 0) count++;
            if (this.selectedPriorities.length > 0) count++;
            return count;
        },

        dateRangeText() {
            if (!this.reportData || !this.reportData.dateRangeStats) return 'Son 7 günde';

            if (this.reportData.dateRangeStats.isFiltered && this.dateRange) {
                const startDate = this.$moment(this.dateRange[0]).format('DD/MM/YYYY');
                const endDate = this.$moment(this.dateRange[1]).format('DD/MM/YYYY');
                return `${startDate} - ${endDate} tarihlerinde`;
            }

            return 'Son 7 günde';
        },

        upcomingDeadlineText() {
            if (!this.reportData || !this.reportData.dateRangeStats) return '0 öğenin bitiş tarihi yaklaştı (önümüzdeki 7 gün)';

            const upcomingCount = this.reportData.dateRangeStats.upcomingDeadlines;

            if (this.reportData.dateRangeStats.isFiltered && this.dateRange) {
                const startDate = this.$moment(this.dateRange[0]).format('DD/MM/YYYY');
                const endDate = this.$moment(this.dateRange[1]).format('DD/MM/YYYY');

                if (upcomingCount > 0) {
                    return `${upcomingCount} öğenin bitiş tarihi var (${startDate} - ${endDate})`;
                } else {
                    return `0 öğenin bitiş tarihi var (${startDate} - ${endDate})`;
                }
            }

            if (upcomingCount > 0) {
                return `${upcomingCount} öğenin bitiş tarihi yaklaştı (önümüzdeki 7 gün)`;
            } else {
                return '0 öğenin bitiş tarihi yaklaştı (önümüzdeki 7 gün)';
            }
        },
    },
    methods: {
        ...mapActions({
            getTaskReports: 'tasks/getTaskReports',
            getWorkFlows: 'tasks/getWorkFlows',
        }),
        handleWorkflowChange(workflowId) {
            this.selectedWorkflowId = workflowId;
            this.reportData = null;
            this.error = null;

            this.dateRange = null;
            this.selectedAssignees = [];
            this.selectedPriorities = [];

            if (workflowId) {
                this.loadReportsWithFilters();
            }
        },
        async loadReports() {
            if (!this.selectedWorkflowId) {
                console.error('Workflow ID seçilmedi');
                return;
            }

            this.isLoading = true;
            this.error = null;
            this.reportData = null;

            try {
                console.log('Rapor yükleniyor - Workflow ID:', this.selectedWorkflowId);
                console.log('Workflow ID tipi:', typeof this.selectedWorkflowId);
                console.log('Workflow ID uzunluğu:', this.selectedWorkflowId.length);

                const objectIdRegex = /^[0-9a-fA-F]{24}$/;
                if (!objectIdRegex.test(this.selectedWorkflowId)) {
                    throw new Error(`Geçersiz Workflow ID formatı: ${this.selectedWorkflowId}`);
                }

                const filters = {
                };

                console.log('API çağrısı yapılıyor:', {
                    workflowId: this.selectedWorkflowId,
                    filters: filters
                });

                const reports = await this.getTaskReports({
                    workflowId: this.selectedWorkflowId,
                    filters: filters,
                });

                console.log('Rapor verileri alındı:', reports);
                this.reportData = reports;

                this.$notification.success({
                    message: 'Başarılı',
                    description: 'Rapor verileri başarıyla yüklendi.',
                });

            } catch (error) {
                console.error('Rapor yüklenirken hata:', error);
                console.error('Error details:', {
                    message: error.message,
                    response: error.response,
                    stack: error.stack
                });

                this.error = error.message || error.response?.data?.message || 'Bilinmeyen bir hata oluştu';

                this.$notification.error({
                    message: 'Hata',
                    description: this.error,
                });
            } finally {
                this.isLoading = false;
            }
        },
        getInitials(name) {
            if (!name) return '?';
            return name
                .split(' ')
                .map(word => word.charAt(0))
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },
        getWorkloadColor(percentage) {
            if (percentage >= 80) return '#dc2626';
            if (percentage >= 60) return '#ea580c';
            if (percentage >= 40) return '#d97706';
            if (percentage >= 20) return '#65a30d';
            return '#16a34a';
        },
        handleDateRangeChange(dates) {
            this.dateRange = dates;
            this.applyFilters();
        },
        handleAssigneesChange(assignees) {
            this.selectedAssignees = assignees;
            this.applyFilters();
        },
        handlePrioritiesChange(priorities) {
            this.selectedPriorities = priorities;
            this.applyFilters();
        },
        clearFilters() {
            this.dateRange = null;
            this.selectedAssignees = [];
            this.selectedPriorities = [];
            this.applyFilters();
        },
        applyFilters() {
            if (!this.selectedWorkflowId) return;

            const filters = {};

            if (this.dateRange && this.dateRange.length === 2) {
                filters.updateDateStart = this.$moment(this.dateRange[0]).format('YYYY-MM-DD');
                filters.updateDateEnd = this.$moment(this.dateRange[1]).format('YYYY-MM-DD');
            }

            if (this.selectedAssignees.length > 0) {
                filters.assignees = this.selectedAssignees;
            }

            if (this.selectedPriorities.length > 0) {
                filters.priorities = this.selectedPriorities;
            }

            console.log('Filtreler uygulanıyor:', filters);
            this.loadReportsWithFilters(filters);
        },
        async loadReportsWithFilters(filters = {}) {
            if (!this.selectedWorkflowId) return;

            this.isLoading = true;
            this.error = null;

            try {
                console.log('Filtreli rapor yükleniyor:', {
                    workflowId: this.selectedWorkflowId,
                    filters
                });

                const reports = await this.getTaskReports({
                    workflowId: this.selectedWorkflowId,
                    filters: filters,
                });

                console.log('Filtreli rapor verileri alındı:', reports);
                this.reportData = reports;

            } catch (error) {
                console.error('Filtreli rapor yüklenirken hata:', error);
                this.error = error.message || error.response?.data?.message || 'Bilinmeyen bir hata oluştu';

                this.$notification.error({
                    message: 'Hata',
                    description: this.error,
                });
            } finally {
                this.isLoading = false;
            }
        },
    },
    watch: {
        selectedWorkflowId(newValue) {
            console.log('Workflow ID değişti:', newValue);
        },
    },
    async mounted() {
        console.log('TaskReports komponenti yüklendi');
        try {
            await this.getWorkFlows();
            console.log('Workflows yüklendi:', this.workflows);
        } catch (error) {
            console.error('Workflows yüklenirken hata:', error);
        }
    },
};
</script>

<style lang="scss" scoped>
.task-reports {
    height: 100%;
    display: flex;
    flex-direction: column;

    .reports-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid #e8e8e8;
        background-color: #fff;

        .reports-title {
            margin: 0;
            font-size: 24px;
            font-weight: 600;
            color: #333;
        }

        .header-controls {
            display: flex;
            align-items: center;
            gap: 16px;
        }

        .workflow-selector {
            display: flex;
            align-items: center;
        }

        .filter-controls {
            display: flex;
            align-items: center;
            gap: 8px;

            .ant-btn {
                display: flex;
                align-items: center;
                gap: 6px;

                &.active {
                    background-color: #e6f7ff;
                    border-color: #1890ff;
                    color: #1890ff;
                }

                .material-icons {
                    font-size: 16px;
                }

                .filter-badge {
                    background: #1890ff;
                    color: white;
                    border-radius: 50%;
                    width: 18px;
                    height: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 11px;
                    font-weight: 600;
                    margin-left: 4px;
                }
            }
        }
    }

    .filters-panel {
        background: #f9fafb;
        border-bottom: 1px solid #e5e7eb;
        padding: 20px;
        animation: slideDown 0.3s ease-out;

        .filter-row {
            display: flex;
            align-items: flex-end;
            gap: 24px;
            flex-wrap: wrap;
        }

        .filter-group {
            display: flex;
            flex-direction: column;
            gap: 8px;

            .filter-label {
                font-size: 13px;
                font-weight: 600;
                color: #374151;
                margin-bottom: 4px;
            }
        }
    }

    @keyframes slideDown {
        0% {
            opacity: 0;
            transform: translateY(-10px);
        }
        100% {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .reports-content {
        flex: 1;
        padding: 20px;
        background-color: #f5f5f5;
        overflow: auto;

        .no-workflow-selected {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;

            .empty-state {
                text-align: center;

                .empty-icon {
                    font-size: 64px;
                    color: #bfbfbf;
                    margin-bottom: 16px;
                }

                h3 {
                    font-size: 18px;
                    color: #666;
                    margin-bottom: 8px;
                }

                p {
                    color: #999;
                    font-size: 14px;
                }
            }
        }

        .loading-state {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            height: 100%;

            .custom-loading {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 24px;

                .loading-svg {
                    width: 120px;
                    height: 120px;
                    animation: float 3s ease-in-out infinite;

                    .loading-circle-bg {
                        opacity: 0.3;
                    }

                    .loading-circle-progress {
                        stroke-dasharray: 314;
                        stroke-dashoffset: 314;
                        animation: progress 2s ease-in-out infinite;
                        transform-origin: center;
                    }

                    .task-icons {
                        animation: fadeInOut 2s ease-in-out infinite;
                    }

                    .floating-dot {
                        animation: float 2s ease-in-out infinite;

                        &.dot-1 {
                            animation-delay: 0s;
                        }

                        &.dot-2 {
                            animation-delay: 0.5s;
                        }

                        &.dot-3 {
                            animation-delay: 1s;
                        }

                        &.dot-4 {
                            animation-delay: 1.5s;
                        }
                    }
                }

                .loading-text {
                    text-align: center;

                    .loading-title {
                        font-size: 20px;
                        font-weight: 600;
                        color: #1f2937;
                        margin-bottom: 8px;
                        animation: pulse 2s ease-in-out infinite;
                    }

                    .loading-subtitle {
                        font-size: 14px;
                        color: #6b7280;
                        animation: pulse 2s ease-in-out infinite 0.5s;
                    }
                }
            }

            @keyframes float {
                0%, 100% {
                    transform: translateY(0px);
                }
                50% {
                    transform: translateY(-10px);
                }
            }

            @keyframes progress {
                0% {
                    stroke-dashoffset: 314;
                    transform: rotate(-90deg);
                }
                50% {
                    stroke-dashoffset: 157;
                    transform: rotate(90deg);
                }
                100% {
                    stroke-dashoffset: 0;
                    transform: rotate(270deg);
                }
            }

            @keyframes fadeInOut {
                0%, 100% {
                    opacity: 0.6;
                }
                50% {
                    opacity: 1;
                }
            }

            @keyframes pulse {
                0%, 100% {
                    opacity: 1;
                }
                50% {
                    opacity: 0.7;
                }
            }
        }

        .reports-data {
            .stats-cards {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                gap: 20px;
                margin-bottom: 30px;
            }

            .report-cards {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 24px;
                margin-bottom: 30px;

                @media (max-width: 1200px) {
                    grid-template-columns: 1fr;
                }
            }

            .report-card {
                background: #fff;
                border-radius: 20px;
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
                border: 1px solid rgba(0, 0, 0, 0.04);
                overflow: hidden;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                position: relative;

                &::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, #3b82f6, #10b981, #f59e0b, #8b5cf6);
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }

                &:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);

                    &::before {
                        opacity: 1;
                    }
                }

                .card-header {
                    padding: 28px 28px 0 28px;
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;

                    .header-content {
                        flex: 1;
                    }

                    .header-icon {
                        width: 48px;
                        height: 48px;
                        background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
                        border-radius: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;

                        .material-icons {
                            font-size: 24px;
                            color: #6b7280;
                        }
                    }

                    .card-title {
                        margin: 0 0 8px 0;
                        font-size: 22px;
                        font-weight: 700;
                        color: #111827;
                        letter-spacing: -0.025em;
                    }

                    .card-subtitle {
                        margin: 0;
                        font-size: 15px;
                        color: #6b7280;
                        line-height: 1.6;
                        font-weight: 400;
                    }
                }

                .card-content {
                    padding: 24px 28px 28px 28px;
                }
            }

            .pipeline-overview {
                .chart-container {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 40px;

                    @media (max-width: 768px) {
                        flex-direction: column;
                        gap: 32px;
                        align-items: center;
                    }
                }

                .simple-pie-chart {
                    width: 180px;
                    height: 180px;
                    flex-shrink: 0;

                    .pie-container {
                        position: relative;
                        width: 100%;
                        height: 100%;

                        .pie-chart {
                            width: 100%;
                            height: 100%;
                            border-radius: 50%;
                            position: relative;
                            animation: pieRotate 1.5s ease-out forwards;
                            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);

                            .pie-center {
                                position: absolute;
                                top: 50%;
                                left: 50%;
                                transform: translate(-50%, -50%);
                                width: 100px;
                                height: 100px;
                                background: #fff;
                                border-radius: 50%;
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                justify-content: center;
                                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                                z-index: 10;

                                .total-number {
                                    font-size: 32px;
                                    font-weight: 800;
                                    color: #111827;
                                    line-height: 1;
                                    margin-bottom: 4px;
                                    letter-spacing: -0.02em;
                                }

                                .total-label {
                                    font-size: 11px;
                                    color: #6b7280;
                                    font-weight: 600;
                                    text-transform: uppercase;
                                    letter-spacing: 0.05em;
                                }
                            }
                        }
                    }
                }

                .legend {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    max-width: 200px;

                    @media (max-width: 768px) {
                        max-width: none;
                        width: 100%;
                    }

                    .legend-item {
                        display: flex;
                        align-items: center;
                        gap: 14px;
                        padding: 12px 16px;
                        border-radius: 12px;
                        background: #f9fafb;
                        transition: all 0.3s ease;
                        animation: fadeInUp 0.6s ease-out forwards;
                        opacity: 0;
                        transform: translateY(10px);

                        &:hover {
                            transform: translateY(-2px);
                            background: #f3f4f6;
                            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                        }

                        .legend-color {
                            width: 14px;
                            height: 14px;
                            border-radius: 3px;
                            flex-shrink: 0;
                            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                        }

                        .legend-text {
                            display: flex;
                            align-items: baseline;
                            gap: 6px;
                            flex: 1;

                            .legend-label {
                                font-size: 15px;
                                color: #374151;
                                font-weight: 600;
                                flex: 1;
                            }

                            .legend-value {
                                font-size: 18px;
                                font-weight: 800;
                                color: #111827;
                                letter-spacing: -0.01em;
                            }
                        }
                    }
                }
            }

            @keyframes fadeInUp {
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }

            .priority-overview {
                .bar-chart-container {
                    height: 300px;
                    padding: 20px 0;
                }

                .chart-wrapper {
                    display: flex;
                    height: 100%;
                    gap: 16px;
                }

                .y-axis {
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    width: 40px;
                    height: 100%;
                    padding-right: 8px;

                    .y-label {
                        font-size: 12px;
                        color: #6b7280;
                        text-align: right;
                        font-weight: 500;
                    }
                }

                .chart-area {
                    flex: 1;
                    position: relative;
                    height: 100%;
                }

                .grid-lines {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;

                    .grid-line {
                        height: 1px;
                        background: #e5e7eb;
                        opacity: 0.6;
                    }
                }

                .bars-container {
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-around;
                    height: 100%;
                    padding: 0 10px;
                    gap: 8px;
                }

                .bar-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    flex: 1;
                    max-width: 60px;
                    animation: slideUp 0.8s ease-out forwards;
                    opacity: 0;
                    transform: translateY(20px);

                    .bar-wrapper {
                        display: flex;
                        align-items: flex-end;
                        height: calc(100% - 30px);
                        width: 100%;
                        position: relative;
                    }

                    .bar {
                        width: 100%;
                        min-height: 4px;
                        border-radius: 6px 6px 0 0;
                        position: relative;
                        transition: all 0.3s ease;
                        animation: growBar 1s ease-out forwards;
                        transform-origin: bottom;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

                        &:hover {
                            transform: scaleY(1.05);
                            filter: brightness(1.1);
                        }

                        .bar-value {
                            position: absolute;
                            top: -24px;
                            left: 50%;
                            transform: translateX(-50%);
                            font-size: 14px;
                            font-weight: 700;
                            color: #111827;
                            background: rgba(255, 255, 255, 0.9);
                            padding: 2px 6px;
                            border-radius: 4px;
                            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                        }
                    }

                    .bar-label {
                        margin-top: 12px;
                        font-size: 13px;
                        font-weight: 600;
                        color: #374151;
                        text-align: center;
                        white-space: nowrap;
                    }
                }
            }

            @keyframes slideUp {
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }

            @keyframes growBar {
                0% {
                    transform: scaleY(0);
                }
                100% {
                    transform: scaleY(1);
                }
            }

            .team-workload {
                .team-list {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .team-member {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 16px 20px;
                    background: #f9fafb;
                    border-radius: 12px;
                    border: 1px solid #e5e7eb;
                    transition: all 0.3s ease;
                    animation: slideInRight 0.6s ease-out forwards;
                    opacity: 0;
                    transform: translateX(20px);
                    cursor: pointer;

                    &:hover {
                        background: #f3f4f6;
                        border-color: #d1d5db;
                        transform: translateX(-2px);
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    }

                    .member-info {
                        display: flex;
                        align-items: center;
                        gap: 16px;
                        flex: 1;
                        min-width: 0;

                        .member-avatar {
                            width: 48px;
                            height: 48px;
                            border-radius: 50%;
                            overflow: hidden;
                            flex-shrink: 0;
                            position: relative;

                            .avatar-image {
                                width: 100%;
                                height: 100%;
                                object-fit: cover;
                            }

                            .avatar-placeholder {
                                width: 100%;
                                height: 100%;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                                color: white;
                                font-weight: 600;
                                font-size: 16px;

                                &.unassigned {
                                    background: linear-gradient(135deg, #6b7280, #4b5563);

                                    .unassigned-icon {
                                        font-size: 24px;
                                    }
                                }

                                .avatar-initials {
                                    font-size: 16px;
                                    font-weight: 700;
                                }
                            }
                        }

                        .member-details {
                            flex: 1;
                            min-width: 0;

                            .member-name {
                                font-size: 16px;
                                font-weight: 600;
                                color: #111827;
                                margin-bottom: 2px;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                            }

                            .member-email {
                                font-size: 13px;
                                color: #6b7280;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                            }
                        }
                    }

                    .workload-info {
                        display: flex;
                        align-items: center;
                        gap: 16px;
                        min-width: 120px;

                        .workload-percentage {
                            font-size: 16px;
                            font-weight: 700;
                            color: #111827;
                            min-width: 40px;
                            text-align: right;
                        }

                        .workload-bar {
                            width: 80px;
                            height: 8px;
                            background: #e5e7eb;
                            border-radius: 4px;
                            overflow: hidden;
                            position: relative;

                            .workload-progress {
                                height: 100%;
                                border-radius: 4px;
                                transition: all 0.8s ease;
                                animation: fillProgress 1.2s ease-out forwards;
                                transform-origin: left;
                                position: relative;

                                &::after {
                                    content: '';
                                    position: absolute;
                                    top: 0;
                                    left: 0;
                                    right: 0;
                                    bottom: 0;
                                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3));
                                    animation: shimmer 2s infinite;
                                }
                            }
                        }
                    }
                }

                .highlight-text {
                    color: #3b82f6;
                    font-weight: 600;
                }
            }

            @keyframes slideInRight {
                to {
                    opacity: 1;
                    transform: translateX(0);
                }
            }

            @keyframes fillProgress {
                0% {
                    transform: scaleX(0);
                }
                100% {
                    transform: scaleX(1);
                }
            }

            @keyframes shimmer {
                0%, 100% {
                    transform: translateX(-100%);
                }
                50% {
                    transform: translateX(100%);
                }
            }

            @keyframes pieRotate {
                0% {
                    transform: scale(0);
                    opacity: 0;
                }
                50% {
                    opacity: 1;
                }
                100% {
                    transform: scale(1);
                    opacity: 1;
                }
            }

            .stat-card {
                background: #fff;
                border-radius: 12px;
                padding: 24px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                border: 1px solid rgba(0, 0, 0, 0.06);
                transition: all 0.3s ease;
                display: flex;
                align-items: center;
                gap: 16px;
                position: relative;
                overflow: hidden;

                &::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 4px;
                    height: 100%;
                    transition: all 0.3s ease;
                }

                &:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
                }

                .stat-icon {
                    width: 56px;
                    height: 56px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;

                    .material-icons {
                        font-size: 28px;
                        color: #fff;
                    }
                }

                .stat-content {
                    flex: 1;
                    min-width: 0;

                    .stat-label {
                        font-size: 13px;
                        color: #6b7280;
                        margin-bottom: 4px;
                        font-weight: 500;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }

                    .stat-value {
                        font-size: 16px;
                        font-weight: 600;
                        color: #1f2937;
                        line-height: 1.3;
                    }
                }

                // Completed card - Green theme
                &.completed {
                    &::before {
                        background: linear-gradient(135deg, #10b981, #059669);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #10b981, #059669);
                    }

                    &:hover::before {
                        width: 6px;
                    }
                }

                // Updated card - Blue theme
                &.updated {
                    &::before {
                        background: linear-gradient(135deg, #3b82f6, #2563eb);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #3b82f6, #2563eb);
                    }

                    &:hover::before {
                        width: 6px;
                    }
                }

                // Created card - Purple theme
                &.created {
                    &::before {
                        background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                    }

                    &:hover::before {
                        width: 6px;
                    }
                }

                // Deadline card - Orange theme
                &.deadline {
                    &::before {
                        background: linear-gradient(135deg, #f59e0b, #d97706);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #f59e0b, #d97706);
                    }

                    &:hover::before {
                        width: 6px;
                    }
                }
            }

        }

        .error-state {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;

            .error-content {
                text-align: center;

                .error-icon {
                    font-size: 64px;
                    color: #ff4d4f;
                    margin-bottom: 16px;
                }

                h3 {
                    font-size: 18px;
                    color: #ff4d4f;
                    margin-bottom: 8px;
                }

                p {
                    color: #666;
                    margin-bottom: 16px;
                }

                .ant-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .material-icons {
                        font-size: 16px;
                    }
                }
            }
        }
    }
}
</style>
